/**
 * Form validation state management hook for EntityForm.
 *
 * Manages the validation lifecycle: Zod schema generation from field config,
 * synchronous field/form validation, debounced async validation, cross-field
 * validation, and error/touched state tracking.
 *
 * Validation timing follows spec 5.2:
 * - On blur: validate field (show error only if touched)
 * - On change after first submit (submitCount > 0): validate on every change
 * - On submit: validate all fields, mark all as touched
 *
 * Cross-field rules (`EntityFormConfig.validation.rules`) run on submit once
 * every visible field passes its own validation, and on blur of one of a
 * rule's `dependsOn` fields (only the rules depending on the blurred field
 * re-run). Their errors are kept apart from the fields' own errors, keyed by
 * rule id, and merged into `errors` below the field's own error.
 */

import { useState, useRef, useMemo, useCallback, useEffect } from 'react';

import { buildFormSchema, normalizeMissingValue } from '../validation/schemaBuilder.ts';
import {
  validateFieldSync,
  validateFormSync,
  evaluateCrossFieldRule,
  createAsyncValidator,
} from '../validation/validators.ts';
import type { CrossFieldError } from '../validation/validators.ts';
import { validationRegistry } from '../validation/registry.ts';
import type { CrossFieldValidator } from '../validation/registry.ts';
import type { FieldConfig, FormValidationConfig, CrossFieldRule } from '../types.ts';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Default debounce delay for async validation (ms). */
const DEFAULT_ASYNC_DEBOUNCE_MS = 400;

/** Shared "no cross-field rules" value, so the rules keep one identity. */
const NO_RULES: readonly CrossFieldRule[] = [];

/** Failing cross-field rules, keyed by rule id. */
type RuleErrors = Record<string, CrossFieldError | undefined>;

/** Shared "no failing rule" value, so clearing it is a no-op state update. */
const NO_RULE_ERRORS: RuleErrors = {};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Return type of the useFormValidation hook. */
export interface UseFormValidationReturn {
  /** Current field errors, keyed by field name. */
  errors: Record<string, string | undefined>;

  /** Fields that have been touched (blurred), keyed by field name. */
  touched: Record<string, boolean>;

  /** Mark a field as touched and trigger its validation. */
  setTouched: (name: string) => void;

  /** Validate a single field. Returns the error message or undefined. */
  validateField: (name: string) => Promise<string | undefined>;

  /** Validate all visible fields and cross-field rules. Returns true if valid. */
  validateForm: () => Promise<boolean>;

  /** Manually set an error on a specific field. */
  setError: (name: string, message: string) => void;

  /** Clear the error on a specific field. */
  clearError: (name: string) => void;

  /** Clear all field errors. */
  clearErrors: () => void;

  /** Whether the form is currently valid (all errors are undefined). */
  isValid: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Check whether a value is considered "empty" for async validation purposes.
 * Empty values skip async validation -- the required rule handles them.
 */
function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  return false;
}

/**
 * Collect all registered cross-field validators referenced by the given rules.
 * Returns a Map suitable for `evaluateCrossFieldRule`. The registry is read
 * when validation runs (not when the form mounts), so a validator registered
 * after the form mounted is still found.
 */
function collectCrossFieldValidators(
  rules: readonly CrossFieldRule[],
): Map<string, CrossFieldValidator> {
  const map = new Map<string, CrossFieldValidator>();

  for (const rule of rules) {
    if (!map.has(rule.validator)) {
      const fn = validationRegistry.getCrossFieldValidator(rule.validator);
      if (fn) {
        map.set(rule.validator, fn);
      }
    }
  }

  return map;
}

/**
 * The values handed to cross-field validators: the visible fields' values --
 * a hidden field is neither validated nor submitted, so it is left out --
 * with every missing value normalised to `undefined` (`normalizeMissingValue`,
 * the rule the field schemas use). Keys that are not fields pass through.
 */
function crossFieldValues(
  values: Record<string, unknown>,
  fields: Record<string, FieldConfig>,
  visibilityMap: Record<string, boolean>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [name, value] of Object.entries(values)) {
    if (visibilityMap[name] !== false) {
      result[name] = normalizeMissingValue(fields[name], value);
    }
  }

  return result;
}

/**
 * Run one cross-field rule, or skip it.
 *
 * - The rule is skipped while one of its `dependsOn` fields is hidden: its
 *   value is not submitted and the user cannot see or fix it.
 * - An error attached to a hidden field, or to a key that is not a field of
 *   the form, is dropped: it would block submission without ever being shown.
 *
 * @returns The error to show, or `null`.
 */
function runCrossFieldRule(
  rule: CrossFieldRule,
  values: Record<string, unknown>,
  fields: Record<string, FieldConfig>,
  visibilityMap: Record<string, boolean>,
  validators: Map<string, CrossFieldValidator>,
): CrossFieldError | null {
  if (rule.dependsOn.some((name) => visibilityMap[name] === false)) {
    return null;
  }

  const error = evaluateCrossFieldRule(values, rule, validators);

  if (error === null || visibilityMap[error.field] === false) {
    return null;
  }

  if (fields[error.field] === undefined) {
    /* v8 ignore start -- false branch unreachable in test env (DEV is always true) */
    if (import.meta.env.DEV) {
      /* v8 ignore stop */
      console.warn(
        `[EntityForm] Cross-field rule "${rule.id}" reported an error on "${error.field}", which is not a field of the form; the error is ignored.`,
      );
    }
    return null;
  }

  return error;
}

/**
 * Run every cross-field rule (on submit).
 *
 * @returns The failing rules keyed by rule id, or `NO_RULE_ERRORS` when none
 *          fails.
 */
function runCrossFieldRules(
  rules: readonly CrossFieldRule[],
  values: Record<string, unknown>,
  fields: Record<string, FieldConfig>,
  visibilityMap: Record<string, boolean>,
): RuleErrors {
  const validators = collectCrossFieldValidators(rules);
  const input = crossFieldValues(values, fields, visibilityMap);
  let failing: RuleErrors | undefined;

  for (const rule of rules) {
    const error = runCrossFieldRule(rule, input, fields, visibilityMap, validators);
    if (error !== null) {
      failing ??= {};
      failing[rule.id] = error;
    }
  }

  return failing ?? NO_RULE_ERRORS;
}

/**
 * The cross-field rules, keyed on their content rather than on the identity
 * of the config object: a form config built inline hands a new
 * `validation` object on every render, which would otherwise give
 * `validateForm` / `validateField` -- and the memoised form API -- a new
 * identity on every render. The returned array only changes when a rule does.
 */
function useStableRules(formValidation: FormValidationConfig | undefined): readonly CrossFieldRule[] {
  const rules = formValidation?.rules;
  const signature = rules !== undefined && rules.length > 0 ? JSON.stringify(rules) : '';

  return useMemo(
    () => (signature === '' ? NO_RULES : (JSON.parse(signature) as CrossFieldRule[])),
    [signature],
  );
}

/**
 * Merge the failing cross-field rules into the fields' own errors. A field's
 * own error wins; among rules, the first failing one in config order wins.
 * With no failing rule, `fieldErrors` itself is returned.
 */
function mergeRuleErrors(
  fieldErrors: Record<string, string | undefined>,
  ruleErrors: RuleErrors,
  rules: readonly CrossFieldRule[],
): Record<string, string | undefined> {
  let merged: Record<string, string | undefined> | undefined;

  for (const rule of rules) {
    const error = ruleErrors[rule.id];
    if (error !== undefined) {
      merged ??= { ...fieldErrors };
      merged[error.field] ??= error.message;
    }
  }

  return merged ?? fieldErrors;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages validation state for an EntityForm.
 *
 * Generates a Zod schema from the field configurations and visibility map,
 * validates individual fields and the full form, handles async validation
 * with debouncing, runs cross-field validators, and tracks error/touched
 * state.
 *
 * @param fields           - All field configurations keyed by field name.
 * @param values           - Current form values keyed by field name.
 * @param visibilityMap    - Map of field name to visibility boolean.
 * @param formValidation   - Optional form-level validation config (cross-field
 *                           rules). Keyed on its content, so a config rebuilt
 *                           on every render is fine.
 * @param requiredMap      - Optional map of field name to its current required
 *                           state (conditions included, from `useFieldConditions`).
 *                           Fields absent from it keep their config's
 *                           `validation.required`.
 * @returns Validation state and actions.
 */
export function useFormValidation(
  fields: Record<string, FieldConfig>,
  values: Record<string, unknown>,
  visibilityMap: Record<string, boolean>,
  formValidation?: FormValidationConfig,
  requiredMap?: Record<string, boolean>,
): UseFormValidationReturn {
  // ---- State ----

  /** The fields' own errors (schema, async, manual `setError`). */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  /** The failing cross-field rules, keyed by rule id. */
  const [ruleErrors, setRuleErrors] = useState<RuleErrors>(NO_RULE_ERRORS);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const rules = useStableRules(formValidation);

  /** Field errors, each field's own error first, then its cross-field error. */
  const errors = useMemo(
    () => mergeRuleErrors(fieldErrors, ruleErrors, rules),
    [fieldErrors, ruleErrors, rules],
  );

  // ---- Refs ----

  /** Debounce timers for async validation, keyed by field name. */
  const asyncTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  /** Cached async validator instances, keyed by field name. */
  const asyncValidatorsRef = useRef<
    Record<string, (value: unknown) => Promise<string | undefined>>
  >({});

  // ---- Memoised Schema ----

  /**
   * The Zod object schema, regenerated when fields, visibility or the
   * required state change. Hidden fields are excluded so they do not block
   * submission; a field made required by a condition is validated as such.
   */
  const schema = useMemo(
    () => buildFormSchema(fields, visibilityMap, requiredMap),
    [fields, visibilityMap, requiredMap],
  );

  // ---- Derived State ----

  /**
   * Whether the form is currently valid.
   * Derived from the errors state: valid when every value is undefined.
   */
  const isValid = useMemo(
    () => Object.values(errors).every((e) => e === undefined),
    [errors],
  );

  // ---- Async Validator Factory ----

  /**
   * Get or create an async validator for the given field.
   * Validators are cached in a ref so that the debounce timer persists
   * across renders.
   */
  const getAsyncValidator = useCallback(
    (fieldName: string) => {
      const fieldConfig = fields[fieldName];
      const asyncConfig = fieldConfig?.validation?.async;

      if (!asyncConfig) {
        return undefined;
      }

      // Reuse existing validator if already created for this field.
      asyncValidatorsRef.current[fieldName] ??= createAsyncValidator(asyncConfig);

      return asyncValidatorsRef.current[fieldName];
    },
    [fields],
  );

  // ---- Cross-field Rules Depending On A Field ----

  /**
   * Re-run the cross-field rules that read `name`, once `name` has been
   * validated. Rules that do not list `name` in `dependsOn` are left alone,
   * so blurring an unrelated field never runs them.
   *
   * A rule runs only when every field it reads passes its own validation.
   * A failing rule's error is recorded only when it targets the blurred
   * field or a field the user has already left (touched; every visible field
   * is touched after a submit), never on a field the user has not reached
   * yet. A passing or skipped rule's error is cleared.
   */
  const revalidateDependentRules = useCallback(
    (name: string, ownError: string | undefined) => {
      const dependents = rules.filter((rule) => rule.dependsOn.includes(name));

      if (dependents.length === 0) {
        return;
      }

      const validators = collectCrossFieldValidators(dependents);
      const input = crossFieldValues(values, fields, visibilityMap);
      const updates: RuleErrors = {};

      for (const rule of dependents) {
        const runnable =
          ownError === undefined &&
          rule.dependsOn.every((dep) => validateFieldSync(dep, values[dep], schema) === undefined);
        const error = runnable
          ? runCrossFieldRule(rule, input, fields, visibilityMap, validators)
          : null;
        const shown = error !== null && (error.field === name || touched[error.field] === true);
        updates[rule.id] = shown ? error : undefined;
      }

      setRuleErrors((prev) => ({ ...prev, ...updates }));
    },
    [rules, values, fields, visibilityMap, schema, touched],
  );

  // ---- Validate Field ----

  /**
   * Validate a single field's own rules against its schema slice.
   *
   * 1. Run synchronous Zod validation.
   * 2. If sync passes AND the field has async validation configured,
   *    run the async validator with debouncing.
   * 3. Update the field's own error and return it (or undefined).
   */
  const validateOwnField = useCallback(
    async (name: string): Promise<string | undefined> => {
      const value = values[name];

      // Step 1: Synchronous validation
      const syncError = validateFieldSync(name, value, schema);

      if (syncError) {
        setFieldErrors((prev) => ({ ...prev, [name]: syncError }));
        return syncError;
      }

      // Step 2: Async validation (if configured)
      const fieldConfig = fields[name];
      const asyncConfig = fieldConfig?.validation?.async;

      if (asyncConfig && !isEmptyValue(value)) {
        // Cancel any pending async validation for this field.
        const existingTimer = asyncTimersRef.current[name];
        if (existingTimer !== undefined) {
          clearTimeout(existingTimer);
          asyncTimersRef.current[name] = undefined as unknown as ReturnType<typeof setTimeout>;
        }

        const debounceMs = asyncConfig.debounceMs ?? DEFAULT_ASYNC_DEBOUNCE_MS;

        // Run debounced async validation
        const asyncError = await new Promise<string | undefined>((resolve) => {
          asyncTimersRef.current[name] = setTimeout(async () => {
            asyncTimersRef.current[name] = undefined as unknown as ReturnType<typeof setTimeout>;

            const asyncValidator = getAsyncValidator(name);
            if (asyncValidator) {
              const result = await asyncValidator(value);
              resolve(result);
            } else {
              resolve(undefined);
            }
          }, debounceMs);
        });

        if (asyncError) {
          setFieldErrors((prev) => ({ ...prev, [name]: asyncError }));
          return asyncError;
        }
      }

      // No errors -- clear any previous error for this field.
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
      return undefined;
    },
    [values, schema, fields, getAsyncValidator],
  );

  /**
   * Validate a single field: its own rules (`validateOwnField`), then the
   * cross-field rules that depend on it. Returns the field's own error; a
   * cross-field error shows up in `errors`.
   */
  const validateField = useCallback(
    async (name: string): Promise<string | undefined> => {
      const ownError = await validateOwnField(name);
      revalidateDependentRules(name, ownError);
      return ownError;
    },
    [validateOwnField, revalidateDependentRules],
  );

  // ---- Validate Form ----

  /**
   * Validate all visible fields against the full Zod schema.
   *
   * 1. Run synchronous validation on all values.
   * 2. If all sync validations pass, run the cross-field rules.
   * 3. Mark all visible fields as touched.
   * 4. Return true if the form is valid, false otherwise.
   */
  const validateForm = useCallback((): Promise<boolean> => {
    // Step 1: Synchronous full-form validation
    const syncErrors = validateFormSync(values, schema);

    // Step 2: Cross-field validation (only if sync passes)
    const hasSyncErrors = Object.values(syncErrors).some((e) => e !== undefined);
    const failingRules =
      hasSyncErrors || rules.length === 0
        ? NO_RULE_ERRORS
        : runCrossFieldRules(rules, values, fields, visibilityMap);

    setFieldErrors(syncErrors);
    setRuleErrors(failingRules);

    // Step 3: Mark all visible fields as touched
    const allTouched: Record<string, boolean> = {};
    for (const fieldName of Object.keys(fields)) {
      if (visibilityMap[fieldName] !== false) {
        allTouched[fieldName] = true;
      }
    }
    setTouched((prev) => ({ ...prev, ...allTouched }));

    // Step 4: Return validity
    return Promise.resolve(!hasSyncErrors && failingRules === NO_RULE_ERRORS);
  }, [values, schema, fields, visibilityMap, rules]);

  // ---- Touched Management ----

  /**
   * Mark a field as touched and trigger its validation.
   */
  const markTouched = useCallback(
    (name: string) => {
      setTouched((prev) => ({ ...prev, [name]: true }));
      void validateField(name);
    },
    [validateField],
  );

  // ---- Manual Error Management ----

  /**
   * Manually set an error on a specific field.
   */
  const setError = useCallback((name: string, message: string) => {
    setFieldErrors((prev) => ({ ...prev, [name]: message }));
  }, []);

  /**
   * Clear the error on a specific field: its own error and any cross-field
   * error attached to it.
   */
  const clearError = useCallback((name: string) => {
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    setRuleErrors((prev) => {
      const kept = Object.entries(prev).filter(([, error]) => error?.field !== name);
      return kept.length === Object.keys(prev).length ? prev : Object.fromEntries(kept);
    });
  }, []);

  /**
   * Clear all field errors (cross-field errors included).
   */
  const clearErrors = useCallback(() => {
    setFieldErrors({});
    setRuleErrors(NO_RULE_ERRORS);
  }, []);

  // ---- Cleanup ----

  /**
   * Cancel all pending async validation timers on unmount.
   */
  useEffect(() => {
    const timersRef = asyncTimersRef;
    return () => {
      const timers = timersRef.current;
      for (const fieldName of Object.keys(timers)) {
        clearTimeout(timers[fieldName]);
      }
    };
  }, []);

  // ---- Return ----
  // Memoised so the whole return object keeps a stable identity and only
  // changes when an underlying member changes. `useEntityForm` depends on
  // this object wholesale in its memo/callback deps, so an unstable literal
  // would defeat that memoisation.

  return useMemo<UseFormValidationReturn>(
    () => ({
      errors,
      touched,
      setTouched: markTouched,
      validateField,
      validateForm,
      setError,
      clearError,
      clearErrors,
      isValid,
    }),
    [
      errors,
      touched,
      markTouched,
      validateField,
      validateForm,
      setError,
      clearError,
      clearErrors,
      isValid,
    ],
  );
}
